import type { FilteredCountries } from '@/hooks/useCountrySelection'
import type { CountryItem } from '@/types/countries'
import { VpCheckbox, VpDivider } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import './country-selector-modal.scss'

interface CountrySelectorModalListProps {
  countries: CountryItem[]
  filteredCountries: FilteredCountries
  hasCountriesError: boolean
  handleToggle: (countryId: number) => void
  handleToggleAll: () => void
  checked: boolean | 'indeterminate'
}

export function CountrySelectorModalList({
  countries,
  filteredCountries,
  hasCountriesError,
  handleToggle,
  handleToggleAll,
  checked,
}: Readonly<CountrySelectorModalListProps>) {
  const { t } = useTranslation()

  return (
    <div className="country-list" data-testid="country-list">
      {hasCountriesError
        ? (
            <div className="country-list-empty" data-testid="country-list-error">
              {t('products-page.country-modal.error')}
            </div>
          )
        : (
            <>
              <ul data-testid="country-list-select-all" className="country-list-select-all">
                <li className="country-list-item" data-testid="country-item-all">
                  <VpCheckbox
                    id="country-checkbox-all"
                    name="country-all"
                    checked={checked}
                    onChange={handleToggleAll}
                  >
                    <span className="country-list-item-name">{t('products-page.all-countries')}</span>
                  </VpCheckbox>
                </li>
              </ul>
              <VpDivider />
              {filteredCountries.pinned.length > 0 && (
                <>
                  <ul className="country-list-section country-list-section--pinned" data-testid="country-list-pinned">
                    {filteredCountries.pinned.map(country => (
                      <li key={country.code} className="country-list-item" data-testid={`country-item-${country.code}`}>
                        <VpCheckbox
                          id={`country-checkbox-${country.id}`}
                          name={`country-${country.id}`}
                          checked={true}
                          onChange={() => handleToggle(country.id)}
                        >
                          <span className="country-list-item-name">{country.name}</span>
                        </VpCheckbox>
                      </li>
                    ))}
                  </ul>
                  <VpDivider />
                </>
              )}
              {filteredCountries.pinned.length > 0 && filteredCountries.unpinned.length > 0 && (
                <div className="country-list-divider" aria-hidden="true" />
              )}
              {filteredCountries.unpinned.length > 0 && (
                <ul className="country-list-section country-list-section--unpinned" data-testid="country-list-unpinned">
                  {filteredCountries.unpinned.map(country => (
                    <li key={country.code} className="country-list-item" data-testid={`country-item-${country.code}`}>
                      <VpCheckbox
                        id={`country-checkbox-${country.id}`}
                        name={`country-${country.id}`}
                        checked={false}
                        onChange={() => handleToggle(country.id)}
                      >
                        <span className="country-list-item-name">{country.name}</span>
                      </VpCheckbox>
                    </li>
                  ))}
                </ul>
              )}
              {filteredCountries.pinned.length === 0 && filteredCountries.unpinned.length === 0 && (
                <div className="country-list-empty" data-testid="country-list-empty">
                  {countries.length === 0
                    ? t('products-page.country-modal.empty')
                    : t('products-page.country-search-no-results')}
                </div>
              )}
            </>
          )}
    </div>
  )
}
